import React from 'react';
import { lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store';

import Login from './pages/Login';
import Setup from './pages/Setup';
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Activities = lazy(() => import('./pages/Activities'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Internet = lazy(() => import('./pages/Internet'));
const UserReport = lazy(() => import('./pages/UserReport'));
const Chat = lazy(() => import('./pages/Chat'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminTenants = lazy(() => import('./pages/AdminTenants'));
const AdminTokenUsage = lazy(() => import('./pages/AdminTokenUsage'));
const AdminImport = lazy(() => import('./pages/AdminImport'));
const AdminCollaborators = lazy(() => import('./pages/AdminCollaborators'));
const AdminSiteCategories = lazy(() => import('./pages/AdminSiteCategories'));
const AdminAppCategories = lazy(() => import('./pages/AdminAppCategories'));
const AdminAIAnalysis = lazy(() => import('./pages/AdminAIAnalysis'));
const AdminChatHistory = lazy(() => import('./pages/AdminChatHistory'));
const SuperadminChatHistory = lazy(() => import('./pages/SuperadminChatHistory'));
const ReportDaily = lazy(() => import('./pages/ReportDaily'));
const ReportWeekly = lazy(() => import('./pages/ReportWeekly'));
const ReportMonthly = lazy(() => import('./pages/ReportMonthly'));
const SettingsGoals = lazy(() => import('./pages/SettingsGoals'));
const FileOperations = lazy(() => import('./pages/FileOperations'));
const AppAnalytics = lazy(() => import('./pages/AppAnalytics'));
const GoalsDashboard = lazy(() => import('./pages/GoalsDashboard'));
const WorkHoursAnalytics = lazy(() => import('./pages/WorkHoursAnalytics'));
const DepartmentsAnalytics = lazy(() => import('./pages/DepartmentsAnalytics'));
const Alerts = lazy(() => import('./pages/Alerts'));
const AISettings = lazy(() => import('./pages/AISettings'));
const AIMetrics = lazy(() => import('./pages/AIMetrics'));
const KeywordSearch = lazy(() => import('./pages/KeywordSearch'));
const KeywordMonitoring = lazy(() => import('./pages/KeywordMonitoring'));
const KeywordOccurrences = lazy(() => import('./pages/KeywordOccurrences'));
const SmtpSettings = lazy(() => import('./pages/SmtpSettings'));

// Layout
import Layout from './components/Layout';

function App() {
  const { isAuthenticated, tenant, user } = useAuthStore();

  return (
    <Router>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/setup" element={<Setup />} />

        <Route
          path="/"
          element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}
        >
          <Route index element={<Dashboard />} />
          <Route path="activities" element={<Activities />} />
          {tenant?.planType >= 2 && (
            <>
              <Route path="analytics" element={<Analytics />} />
              <Route path="analytics/work-hours" element={<WorkHoursAnalytics />} />
              <Route path="analytics/departments" element={<DepartmentsAnalytics />} />
              <Route path="internet" element={<Internet />} />
              <Route path="file-operations" element={<FileOperations />} />
              <Route path="app-analytics" element={<AppAnalytics />} />
              <Route path="user-report" element={<UserReport />} />
              <Route path="reports/daily" element={<ReportDaily />} />
              <Route path="reports/weekly" element={<ReportWeekly />} />
              <Route path="reports/monthly" element={<ReportMonthly />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="keyword-search" element={<KeywordSearch />} />
              <Route path="keyword-monitoring" element={<KeywordMonitoring />} />
              <Route path="keyword-occurrences" element={<KeywordOccurrences />} />
            </>
          )}
          {tenant?.planType >= 3 && (
            <Route path="chat" element={<Chat />} />
          )}
          <Route path="settings" element={<Settings />} />
          <Route path="settings/goals" element={<SettingsGoals />} />
          <Route path="settings/ai" element={<AISettings />} />
          <Route path="goals/dashboard" element={<GoalsDashboard />} />

          {/* Rotas de admin e superadmin */}
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <>
              {/* Colaboradores e Importação - para admin e superadmin */}
              <Route path="admin/colaboradores" element={<AdminCollaborators />} />
              <Route path="admin/import" element={<AdminImport />} />
              <Route path="admin/site-categories" element={<AdminSiteCategories />} />
              <Route path="admin/app-categories" element={<AdminAppCategories />} />
              <Route path="admin/ai-metrics" element={<AIMetrics />} />
              <Route path="admin/smtp-settings" element={<SmtpSettings />} />
              <Route path="admin/chat-history" element={<AdminChatHistory />} />
            </>
          )}

          {/* Rotas apenas para superadmin */}
          {user?.role === 'superadmin' && (
            <>
              <Route path="admin/users" element={<AdminUsers />} />
              <Route path="admin/tenants" element={<AdminTenants />} />
              <Route path="admin/token-usage/:tenantId" element={<AdminTokenUsage />} />
              <Route path="admin/ai-analysis" element={<AdminAIAnalysis />} />
              <Route path="admin/chat-history-all" element={<SuperadminChatHistory />} />
            </>
          )}
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
